import { Checkbox, Dialog, GridRow, GridCell, TextField, Typography } from "@episerver/ui-framework";
import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Address, AddressType, InView } from "../model/address";
import { formSelector, initialAddressFormState, onOpenDialog } from "../reducers/addressFormReducer";
import { fetchAddressById } from "../reducers/asyncThunks";
import { onAddOrUpdateContactAddress } from "../../ContactManager/reducers/contactDetailReducer";
import { onAddOrUpdateOrganizationAddress } from "../../OrganizationManager/reducers/organizationDetailReducer";
import { Controller, useForm } from "react-hook-form";
import { Property } from "../../../businessFoundation/MetaClassData/MetaClassDataList/MetaClassDataModel";
import ExtendedProperty from "../../ExtendedProperty/ExtendedProperty";

interface AddressProps {
    inView?: InView;
}

const AddressForm = (props: AddressProps) => {
    const dispatch = useAppDispatch();
    const { inView = InView.None } = props;
    const form = useAppSelector(formSelector);
    const { address: initialAddress } = form;

    const initComponent = async () => {
        if (!initialAddress.isFetched) {
            dispatch(fetchAddressById(initialAddress.primaryKeyId ?? ""));
        }
    };

    useEffect(() => {
        if (form.open) {
            initComponent();
        }
    }, [initialAddress.primaryKeyId, form.open]);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        setError,
        clearErrors,
        trigger,
        reset,
        formState: { errors, isValid, isDirty },
    } = useForm<Address>({
        reValidateMode: "onChange",
        mode: "onChange",
        defaultValues: initialAddress,
    });

    const values = watch();

    useEffect(() => {
        reset(initialAddress);
    }, [initialAddress]);

    const onSubmit = async (address: Address) => {
        if (inView == InView.Contact) {
            dispatch(onAddOrUpdateContactAddress(address));
        } else if (inView == InView.Organization) {
            dispatch(onAddOrUpdateOrganizationAddress(address));
        }
        dispatch(onOpenDialog(initialAddressFormState));
    };

    const onChangeCheckbox = (e: any) => {
        e.persist();
        let { name, checked, value } = e.target;
        if (checked) {
            setValue(name, [...values.addressType, Number(value)], { shouldValidate: true });
        } else {
            setValue(
                name,
                values.addressType.filter((a: any) => a != Number(value)),
                { shouldValidate: true }
            );
        }
    };

    return (
        <Dialog
            className="address-form"
            open={form.open}
            title="Address form"
            dismissLabel="Cancel"
            confirmLabel="Save"
            enableConfirm={isDirty && isValid}
            onInteraction={(e) => {
                if (e.detail.action === "confirm") {
                    handleSubmit(onSubmit)();
                } else {
                    dispatch(onOpenDialog(initialAddressFormState));
                }
            }}
        >
            <form
                autoComplete="off"
                onSubmit={(e) => {
                    e.preventDefault();
                }}
            >
                <GridRow className="address-form-control">
                    <GridCell span={6}>
                        <Controller
                            name="name"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: "The name is required",
                                },
                            }}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="name"
                                    autoFocus
                                    maxLength={128}
                                    outlined
                                    label="Name (*)"
                                    name="name"
                                    helperText={errors.name?.message ?? ""}
                                    invalid={!!errors.name?.message}
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="lastName"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="lastName"
                                    outlined
                                    maxLength={64}
                                    label="Last Name"
                                    name="lastName"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="firstName"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="firstName"
                                    outlined
                                    maxLength={64}
                                    label="First Name"
                                    name="firstName"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="daytimePhoneNumber"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="daytimePhoneNumber"
                                    outlined
                                    maxLength={32}
                                    label="Day Phone"
                                    name="daytimePhoneNumber"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="eveningPhoneNumber"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="eveningPhoneNumber"
                                    outlined
                                    maxLength={32}
                                    label="Evening Phone"
                                    name="eveningPhoneNumber"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                pattern: {
                                    value: /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/i,
                                    message: "The email is invalid",
                                },
                            }}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="email"
                                    outlined
                                    maxLength={256}
                                    label="Email"
                                    name="email"
                                    helperText={errors.email?.message ?? ""}
                                    invalid={!!errors.email?.message}
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="line1"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="line1"
                                    outlined
                                    maxLength={80}
                                    label="Line #1"
                                    name="line1"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="line2"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="line2"
                                    outlined
                                    maxLength={80}
                                    label="Line #2"
                                    name="line2"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="city"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="city"
                                    outlined
                                    maxLength={64}
                                    label="City"
                                    name="city"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="state"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="state"
                                    outlined
                                    maxLength={64}
                                    label="State"
                                    name="state"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="countryCode"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="countryCode"
                                    outlined
                                    maxLength={50}
                                    label="Country Code"
                                    name="countryCode"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="countryName"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="countryName"
                                    outlined
                                    maxLength={50}
                                    label="Country Name"
                                    name="countryName"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="postalCode"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="postalCode"
                                    outlined
                                    maxLength={20}
                                    label="Postal Code"
                                    name="postalCode"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <GridCell span={6}>
                        <Controller
                            name="regionCode"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    id="regionCode"
                                    outlined
                                    maxLength={50}
                                    label="Region Code"
                                    name="regionCode"
                                    value={value ?? ""}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </GridCell>
                    <>
                        {values.extendedProperties?.map((property: Property, index: number) => {
                            return (
                                <GridCell span={6} key={index}>
                                    <div className="control">
                                        <ExtendedProperty
                                            index={index}
                                            className="Address"
                                            property={property}
                                            clearErrors={clearErrors}
                                            control={control}
                                            errors={errors}
                                            setError={setError}
                                            setValue={setValue}
                                            trigger={trigger}
                                            key={index}
                                        />
                                    </div>
                                </GridCell>
                            );
                        })}
                    </>
                </GridRow>
                <GridCell span={12}>
                    <br />
                    <Typography use="caption">Type (*)</Typography>
                    <Controller
                        name="addressType"
                        control={control}
                        rules={{
                            validate: {
                                [`addressType`]: (v) => (v && v.length > 0) || "You have to choose at least one type",
                            },
                        }}
                        render={({}) => (
                            <>
                                <div className="checkbox-group">
                                    <Checkbox
                                        name="addressType"
                                        value={AddressType.Public}
                                        checked={values.addressType?.indexOf(AddressType.Public) >= 0}
                                        onChange={(e: any) => {
                                            onChangeCheckbox(e);
                                        }}
                                    >
                                        Public
                                    </Checkbox>
                                    <Checkbox
                                        name="addressType"
                                        checked={values.addressType?.indexOf(AddressType.Shipping) >= 0}
                                        value={AddressType.Shipping}
                                        onChange={(e: any) => {
                                            onChangeCheckbox(e);
                                        }}
                                    >
                                        Shipping
                                    </Checkbox>
                                    <Checkbox
                                        name="addressType"
                                        checked={values.addressType?.indexOf(AddressType.Billing) >= 0}
                                        value={AddressType.Billing}
                                        onChange={(e: any) => {
                                            onChangeCheckbox(e);
                                        }}
                                    >
                                        Billing
                                    </Checkbox>
                                </div>
                                <div className={!!errors.addressType?.message ? "invalid-message" : "hidden"}>
                                    {errors.addressType?.message}
                                </div>
                            </>
                        )}
                    />
                    <br />
                </GridCell>
            </form>
        </Dialog>
    );
};

export default AddressForm;
